import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { ProductEntry } from "@/types/product/productStep.types";

export const useProductFormValidation = (activityType?: string) => {
  const { t } = useLanguage();
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateField = (field: string, value: any, formEntry?: ProductEntry): string => {
    switch (field) {
      case "name":
        if (!value || value.trim() === "") {
          return t("validation.required", { field: t("products.productName") });
        }
        break;
      case "stoneType":
        if (!value) {
          return t("validation.required", { field: t("footprints.generalData.stoneType") });
        }
        break;
      case "productType":
        if (!value) {
          return t("validation.required", { field: t("products.productType") });
        }
        break;
      case "functionalUnitQuantity":
        if (value === "" || value === null || value === undefined) {
          return t("validation.required", { field: t("products.functionalUnitQuantity") });
        }
        // Handle both string and number values
        if (typeof value === "string") {
          const numericValue = parseFloat(value);
          if (isNaN(numericValue)) {
            return t("validation.positiveNumber", { field: t("products.functionalUnitQuantity") });
          }
          if (numericValue <= 0) {
            return t("validation.positiveNumber", { field: t("products.functionalUnitQuantity") });
          }
        } else if (typeof value === "number" && value <= 0) {
          return t("validation.positiveNumber", { field: t("products.functionalUnitQuantity") });
        }
        break;
      case "functionalUnitUnit":
        if (!value) {
          return t("validation.required", { field: t("products.functionalUnitUnit") });
        }
        break;
      case "annualProduction":
        if (value === "" || value === null || value === undefined) {
          return t("validation.required", { field: t("products.quantityProducedInStudyYear") });
        }
        // Handle both string and number values
        if (typeof value === "string") {
          const numericValue = parseFloat(value);
          if (isNaN(numericValue)) {
            return t("validation.positiveNumber", { field: t("products.quantityProducedInStudyYear") });
          }
          if (numericValue <= 0) {
            return t("validation.positiveNumber", { field: t("products.quantityProducedInStudyYear") });
          }
        } else if (typeof value === "number" && value <= 0) {
          return t("validation.positiveNumber", { field: t("products.quantityProducedInStudyYear") });
        }
        break;
      case "productRevenue":
        if (value === "" || value === null || value === undefined) {
          return t("validation.required", { field: t("products.productRevenue") });
        }
        // Handle both string and number values
        if (typeof value === "string") {
          const numericValue = parseFloat(value);
          if (isNaN(numericValue)) {
            return t("validation.positiveNumber", { field: t("products.productRevenue") });
          }
          if (numericValue <= 0) {
            return t("validation.positiveNumber", { field: t("products.productRevenue") });
          }
        } else if (typeof value === "number" && value <= 0) {
          return t("validation.positiveNumber", { field: t("products.productRevenue") });
        }
        break;
      case "materialsUsed":
        if (!value || value.length === 0) {
          return t("validation.required", { field: t("products.materialsUsedInProduction") });
        }
        break;
      // Transformation-specific validation
      case "stoneOrigin":
        if (
          (activityType === "transformation" || activityType === "extraction_and_transformation") &&
          (!value || value.trim() === "")
        ) {
          return t("validation.required", { field: t("products.stoneOrigin") });
        }
        break;
      case "originCompanyId":
        if (
          (activityType === "transformation" || activityType === "extraction_and_transformation") &&
          ((formEntry?.stoneOrigin === "associated_company" && activityType === "transformation") ||
            (formEntry?.usesExternalStone === "yes" &&
              formEntry?.comesFromAssociated === "yes" &&
              activityType === "extraction_and_transformation")) &&
          (!value || value.trim() === "")
        ) {
          return t("validation.required", { field: t("products.originCompany") });
        }
        break;
      case "originProductId":
        if (
          (activityType === "transformation" || activityType === "extraction_and_transformation") &&
          ((formEntry?.stoneOrigin === "associated_company" && activityType === "transformation") ||
            (formEntry?.usesExternalStone === "yes" &&
              formEntry?.comesFromAssociated === "yes" &&
              activityType === "extraction_and_transformation")) &&
          formEntry?.originCompanyId &&
          (!value || value.trim() === "")
        ) {
          return t("validation.required", { field: t("products.originProduct") });
        }
        break;
      case "transportType":
        if (
          (activityType === "transformation" || activityType === "extraction_and_transformation") &&
          (!value || value.trim() === "")
        ) {
          return t("validation.required", { field: t("products.transportType") });
        }
        break;
      case "transportDistance":
        if (
          (activityType === "transformation" || activityType === "extraction_and_transformation")
        ) {
          if (value === "" || value === null || value === undefined) {
            return t("validation.required", { field: t("products.transportDistance") });
          }
          if (typeof value === "number" && value <= 0) {
            return t("validation.positiveNumber", { field: t("products.transportDistance") });
          }
        }
        break;
      case "transports":
        if (activityType === "transformation" || activityType === "extraction_and_transformation") {
          const legs = (value as Array<{ vehicleType?: string; distance?: number }> | undefined) ?? [];
          if (legs.length === 0) {
            return t("validation.required", { field: t("products.transportType") });
          }
          for (const leg of legs) {
            if (!leg.vehicleType || String(leg.vehicleType).trim() === "") {
              return t("validation.required", { field: t("products.transportType") });
            }
            const dist = typeof leg.distance === "string" ? parseFloat(leg.distance) : leg.distance;
            if (dist === undefined || dist === null || isNaN(dist as number)) {
              return t("validation.required", { field: t("products.transportDistance") });
            }
            if ((dist as number) <= 0) {
              return t("validation.positiveNumber", { field: t("products.transportDistance") });
            }
          }
        }
        break;
      // Mixed activity specific validation for external stone
      case "usesExternalStone":
        if (activityType === "extraction_and_transformation" && (!value || value.trim() === "")) {
          return t("validation.required", { field: t("products.usesExternalStone") });
        }
        break;
      case "comesFromAssociated":
        if (
          activityType === "extraction_and_transformation" &&
          formEntry?.usesExternalStone === "yes" &&
          (!value || value.trim() === "")
        ) {
          return t("validation.required", { field: t("products.comesFromAssociatedCompany") });
        }
        break;
    }
    return "";
  };

  const validateForm = (formEntry: ProductEntry): boolean => {
    const newErrors: Record<string, string> = {};

    newErrors.name = validateField("name", formEntry.name);
    newErrors.stoneType = validateField("stoneType", formEntry.stoneType);
    newErrors.productType = validateField("productType", formEntry.productType);
    newErrors.functionalUnitQuantity = validateField("functionalUnitQuantity", formEntry.functionalUnit.quantity);
    newErrors.functionalUnitUnit = validateField("functionalUnitUnit", formEntry.functionalUnit.unit);
    newErrors.annualProduction = validateField("annualProduction", formEntry.annualProduction);
    newErrors.productRevenue = validateField("productRevenue", formEntry.productRevenue);
    newErrors.materialsUsed = validateField("materialsUsed", formEntry.materialsUsed);

    // Transformation-specific validation
    if (activityType === "transformation") {
      newErrors.stoneOrigin = validateField("stoneOrigin", formEntry.stoneOrigin, formEntry);
      newErrors.originCompanyId = validateField("originCompanyId", formEntry.originCompanyId, formEntry);
      newErrors.originProductId = validateField("originProductId", formEntry.originProductId, formEntry);
      newErrors.transports = validateField("transports", formEntry.transports, formEntry);
    }

    // Mixed activity validation
    if (activityType === "extraction_and_transformation") {
      newErrors.usesExternalStone = validateField("usesExternalStone", formEntry.usesExternalStone, formEntry);
      newErrors.comesFromAssociated = validateField("comesFromAssociated", formEntry.comesFromAssociated, formEntry);
      newErrors.originCompanyId = validateField("originCompanyId", formEntry.originCompanyId, formEntry);
      newErrors.originProductId = validateField("originProductId", formEntry.originProductId, formEntry);
      newErrors.transports = validateField("transports", formEntry.transports, formEntry);
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  const handleFieldBlur = (field: string, formEntry: ProductEntry) => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    let fieldValue;
    if (field === "functionalUnitQuantity") {
      fieldValue = formEntry.functionalUnit.quantity;
    } else if (field === "functionalUnitUnit") {
      fieldValue = formEntry.functionalUnit.unit;
    } else {
      fieldValue = (formEntry as any)[field];
    }

    const error = validateField(field, fieldValue, formEntry);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const clearFieldError = (field: string, value: any, formEntry: ProductEntry) => {

    const error = validateField(field, value, formEntry);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  // Validate the (possibly multi-leg) transport array, falling back to the
  // legacy single-transport fields for footprints saved before multi-transport.
  const areTransportsValid = (formEntry: ProductEntry): boolean => {
    const legs =
      formEntry.transports && formEntry.transports.length > 0
        ? formEntry.transports
        : formEntry.transportType || formEntry.transportDistance
        ? [{ vehicleType: formEntry.transportType, distance: formEntry.transportDistance }]
        : [];
    if (legs.length === 0) return false;
    return legs.every((l) => !!l.vehicleType && (Number(l.distance) || 0) > 0);
  };

  // Check if form is currently valid (without setting errors)
  const isFormValid = (formEntry: ProductEntry): boolean => {
    const checks: Record<string, boolean> = {
      name: !!(formEntry.name && formEntry.name.trim() !== ""),
      stoneType: !!formEntry.stoneType,
      productType: !!formEntry.productType,
      functionalUnitQuantity: formEntry.functionalUnit.quantity > 0,
      functionalUnitUnit: !!formEntry.functionalUnit.unit,
      annualProduction: formEntry.annualProduction > 0,
      productRevenue: formEntry.productRevenue > 0,
      materialsUsed: !!(formEntry.materialsUsed && formEntry.materialsUsed.length > 0),
    };

    // Add transformation-specific checks
    if (activityType === "transformation") {
      checks.stoneOrigin = !!formEntry.stoneOrigin;

      // Only validate origin company/product if associated_company is selected
      if (formEntry.stoneOrigin === "associated_company") {
        checks.originCompanyId = !!formEntry.originCompanyId;
        if (formEntry.originCompanyId) {
          checks.originProductId = !!formEntry.originProductId;
          checks.transports = areTransportsValid(formEntry);
        }
      }
    }

    // Add mixed activity checks
    if (activityType === "extraction_and_transformation") {
      checks.usesExternalStone = !!formEntry.usesExternalStone;

      // Only validate external stone flow if uses external stone
      if (formEntry.usesExternalStone === "yes") {
        checks.comesFromAssociated = !!formEntry.comesFromAssociated;

        // Only validate origin company/product if associated_company is selected
        if (formEntry.comesFromAssociated === "yes") {
          checks.originCompanyId = !!formEntry.originCompanyId;
          if (formEntry.originCompanyId) {
            checks.originProductId = !!formEntry.originProductId;
            checks.transports = areTransportsValid(formEntry);
          }
        }
      }
    }

    return Object.values(checks).every((check) => check);
  };

  const markAllFieldsTouched = (additionalFields: string[] = []) => {
    const baseFields = [
      "name",
      "stoneType",
      "productType",
      "functionalUnitQuantity",
      "functionalUnitUnit",
      "annualProduction",
      "productRevenue",
      "materialsUsed",
    ];

    const allFields = [...baseFields, ...additionalFields];
    const touchedFields: Record<string, boolean> = {};

    allFields.forEach((field) => {
      touchedFields[field] = true;
    });

    setTouched(touchedFields);
  };

  return {
    touched,
    errors,
    validateField,
    validateForm,
    handleFieldBlur,
    clearFieldError,
    isFormValid,
    markAllFieldsTouched,
    setTouched,
    setErrors,
  };
};
